import { escapeRegExp } from "lodash"
import moment from "moment"
import React, { createContext, useMemo } from "react"
import { DateFormats } from "~/enums/languages"
import useLocaleStorage from "~/hooks/useLocaleStorage"
import { DATE_FORMAT } from "~/constants/dateFormat"

type TimeSettings = {
  dateFormat: string
  dateClockFormat: "12" | "24"
}

type DateSettingsContextType = {
  isHour12: boolean
  locale: string
  timeSettings: TimeSettings
  setTimeSettings: (settings: TimeSettings) => void
  dateFormat: string
  hourFormat: string
  fullDateFormat: string
}

const dateFormatByLanguage: Record<string, DateFormats> = {
  cs: DateFormats.CS_CZ,
}

const getMomentLocale = (locale: string): string => (locale === DateFormats.CS_CZ ? "cs" : locale)

const getDayPeriod = (locale: string, hour: number): string => {
  const time = new Date()
  time.setHours(hour, 0, 0, 0)
  return (
    new Intl.DateTimeFormat(locale, { hour: "numeric", hour12: true })
      .formatToParts(time)
      .find(({ type }) => type === "dayPeriod")?.value || (hour < 12 ? "AM" : "PM")
  )
}

const getSystemDateFormat = (navLang: string): string => {
  const dateFormatValues = Object.values<string>(DateFormats)
  if (dateFormatValues.includes(navLang)) {
    return navLang
  }

  const langPart = navLang.split("-")[0]
  const localeFromLangPart = dateFormatByLanguage[langPart] || langPart
  if (dateFormatValues.includes(localeFromLangPart)) {
    return localeFromLangPart
  }

  return DateFormats.EN_GB
}

/**
 * React Context that provides date and time formatting settings
 * based on the user's preferences (e.g., 12/24-hour format, locale).
 */
export const DateSettingsContext = createContext<DateSettingsContextType | undefined>(undefined)

/**
 * React Provider component that manages and exposes date/time formatting settings.
 *
 * This provider:
 * - Reads the user’s settings from local storage
 * - Falls back to system defaults if not set
 * - Computes common date/time formatting strings
 * - Makes these values accessible to consumers via `DateSettingsContext`
 *
 * @example
 * <DateSettingsProvider>
 *   <App />
 * </DateSettingsProvider>
 */
export const DateSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  /**
   * Load persisted time settings or use fallback defaults.
   * Stores preferences such as:
   * - Whether to use 12- or 24-hour clock
   * - Which locale to use for formatting
   */
  const [timeSettings, setTimeSettings] = useLocaleStorage<TimeSettings>("timeSettings", {
    dateFormat: DateFormats.SYSTEM,
    dateClockFormat: "24",
  })

  /** Whether to format time using a 12-hour clock (true) or 24-hour clock (false) */
  const isHour12 = timeSettings.dateClockFormat === "12"

  /** Use system/browser locale if "system" is selected, otherwise use stored format */
  let locale = timeSettings.dateFormat
  if (timeSettings.dateFormat === DateFormats.SYSTEM) {
    locale = getSystemDateFormat(navigator.language)
  }
  const momentLocale = getMomentLocale(locale)

  // Set the moment.js locale globally to ensure correct weekday abbreviations
  moment.locale(momentLocale)

  const parts = new Intl.DateTimeFormat(locale, DATE_FORMAT).formatToParts()

  const momentFormatPattern = parts
    .map(part => {
      switch (part.type) {
        case "day":
          return "DD"
        case "month":
          return "MM"
        case "year":
          return "YYYY"
        default:
          return part.value
      }
    })
    .join("")
    .trim()
  const amLabel = getDayPeriod(locale, 1)
  const pmLabel = getDayPeriod(locale, 13)

  moment.updateLocale(momentLocale, {
    longDateFormat: {
      L: momentFormatPattern,
    },
    isPM: input => input.toLocaleLowerCase(locale) === pmLabel.toLocaleLowerCase(locale),
    meridiem: hour => (hour < 12 ? amLabel : pmLabel),
    meridiemParse: new RegExp(`${escapeRegExp(amLabel)}|${escapeRegExp(pmLabel)}`, "i"),
  })

  /** Date format string for the selected locale (e.g., "MM/DD/YYYY") */
  const dateFormat = moment.localeData(momentLocale).longDateFormat("L")
  /** Hour format string based on 12/24-hour preference (e.g., "hh:mm A" or "HH:mm") */
  const hourFormat = isHour12 ? "hh:mm A" : "HH:mm"

  /** Combined date and time format string (e.g., "MM/DD/YYYY HH:mm") */
  const value = useMemo(
    () => ({
      isHour12,
      locale,
      timeSettings,
      setTimeSettings,
      dateFormat,
      hourFormat,
      fullDateFormat: `${dateFormat} ${hourFormat}`,
    }),
    [locale, isHour12, timeSettings, setTimeSettings, dateFormat, hourFormat]
  )

  return <DateSettingsContext.Provider value={value}>{children}</DateSettingsContext.Provider>
}
